import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Lock, TrendingUp, TrendingDown, AlertTriangle, Zap, Wheat, Fuel,
  Ship, Cpu, Plane, Building2, Loader2, ChevronRight, Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/* ─── Conflict → Sector Matrix ─── */
type Severity = "critical" | "high" | "moderate";

interface SectorImpact {
  sector: string;
  severity: Severity;
  description: string;
  relatedSymbols: string[];
  icon: any;
}

interface Conflict {
  id: string;
  name: string;
  region: string;
  sectors: SectorImpact[];
}

const CONFLICTS: Conflict[] = [
  {
    id: "ukraine-russia",
    name: "Ukraine–Russia",
    region: "Eastern Europe",
    sectors: [
      { sector: "Energy", severity: "critical", description: "Gas pipeline disruptions, oil export sanctions, refinery strikes", relatedSymbols: ["BZ=F", "CL=F", "NG=F"], icon: Fuel },
      { sector: "Agriculture", severity: "critical", description: "Black Sea grain corridor blockades, fertilizer shortages", relatedSymbols: ["ZW=F"], icon: Wheat },
      { sector: "Semiconductors", severity: "high", description: "Neon gas supply (50% global) disrupted, chip production affected", relatedSymbols: [], icon: Cpu },
      { sector: "Shipping", severity: "high", description: "Black Sea/Azov Sea maritime routes severely restricted", relatedSymbols: [], icon: Ship },
      { sector: "Defense", severity: "high", description: "NATO defense spending surge, arms manufacturing boom", relatedSymbols: [], icon: Building2 },
      { sector: "Aviation", severity: "moderate", description: "Airspace closures across Eastern Europe, rerouting costs", relatedSymbols: [], icon: Plane },
    ],
  },
  {
    id: "israel-gaza",
    name: "Israel–Gaza",
    region: "Middle East",
    sectors: [
      { sector: "Energy", severity: "critical", description: "Strait of Hormuz risk, Houthi attacks on Red Sea shipping", relatedSymbols: ["BZ=F", "CL=F"], icon: Fuel },
      { sector: "Shipping", severity: "critical", description: "Suez Canal diversion, 30% global trade rerouted via Cape", relatedSymbols: [], icon: Ship },
      { sector: "Aviation", severity: "high", description: "Ben Gurion closures, regional airspace restrictions", relatedSymbols: [], icon: Plane },
      { sector: "Tourism", severity: "high", description: "Regional tourism collapse, hotel/airline cancellations", relatedSymbols: [], icon: Building2 },
      { sector: "Agriculture", severity: "moderate", description: "Food supply disruption in Gaza, regional price spikes", relatedSymbols: ["ZW=F"], icon: Wheat },
    ],
  },
  {
    id: "sudan",
    name: "Sudan Civil War",
    region: "East Africa",
    sectors: [
      { sector: "Agriculture", severity: "critical", description: "Breadbasket destruction, famine conditions", relatedSymbols: ["ZW=F"], icon: Wheat },
      { sector: "Energy", severity: "high", description: "Oil infrastructure damage, pipeline shutdowns", relatedSymbols: ["CL=F"], icon: Fuel },
      { sector: "Shipping", severity: "moderate", description: "Red Sea proximity, Port Sudan disruptions", relatedSymbols: [], icon: Ship },
    ],
  },
  {
    id: "myanmar",
    name: "Myanmar Civil War",
    region: "Southeast Asia",
    sectors: [
      { sector: "Semiconductors", severity: "high", description: "Rare earth mineral supply risk, China border instability", relatedSymbols: [], icon: Cpu },
      { sector: "Energy", severity: "moderate", description: "Natural gas pipelines to China/Thailand at risk", relatedSymbols: ["NG=F"], icon: Fuel },
      { sector: "Shipping", severity: "moderate", description: "Andaman Sea route disruptions", relatedSymbols: [], icon: Ship },
    ],
  },
  {
    id: "iran-tensions",
    name: "Iran Tensions",
    region: "Middle East / Persian Gulf",
    sectors: [
      { sector: "Energy", severity: "critical", description: "Strait of Hormuz chokepoint — 21% of global oil transit", relatedSymbols: ["BZ=F", "CL=F", "NG=F"], icon: Fuel },
      { sector: "Shipping", severity: "critical", description: "Persian Gulf tanker seizures, insurance premiums +300%", relatedSymbols: [], icon: Ship },
      { sector: "Aviation", severity: "high", description: "Airspace closures over Iran, Iraq, Persian Gulf", relatedSymbols: [], icon: Plane },
      { sector: "Defense", severity: "high", description: "US/Israel/Gulf military buildup, arms race acceleration", relatedSymbols: [], icon: Building2 },
    ],
  },
  {
    id: "taiwan-strait",
    name: "Taiwan Strait",
    region: "Indo-Pacific",
    sectors: [
      { sector: "Semiconductors", severity: "critical", description: "TSMC produces 90% of advanced chips — global tech collapse risk", relatedSymbols: [], icon: Cpu },
      { sector: "Shipping", severity: "critical", description: "50% of global container traffic passes through Taiwan Strait", relatedSymbols: [], icon: Ship },
      { sector: "Energy", severity: "high", description: "LNG supply to Japan/Korea disrupted, Asian energy crisis", relatedSymbols: ["NG=F"], icon: Fuel },
      { sector: "Aviation", severity: "high", description: "Asia-Pacific airspace closures, massive rerouting", relatedSymbols: [], icon: Plane },
    ],
  },
];

const SEVERITY_COLORS: Record<Severity, string> = {
  critical: "bg-red-500/20 text-red-400 border-red-500/30",
  high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  moderate: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
};

const SEVERITY_LABELS: Record<Severity, string> = {
  critical: "CRITICAL",
  high: "HIGH",
  moderate: "MODERATE",
};

/* ─── Market data hook (reuse same endpoint as MarketRadar) ─── */
function useMarketData() {
  return useQuery({
    queryKey: ["market-data-econ"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("fetch-market-data");
      if (error) throw error;
      return data?.quotes ?? [];
    },
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/* ─── Component ─── */
export default function ConflictEconomicImpact() {
  const { isPremium } = useAuth();
  const [selected, setSelected] = useState(CONFLICTS[0].id);
  const [scenarios, setScenarios] = useState<any>(null);
  const { data: marketQuotes } = useMarketData();

  const conflict = CONFLICTS.find((c) => c.id === selected)!;
  const freeLimit = 2;
  const visibleConflicts = isPremium ? CONFLICTS : CONFLICTS.slice(0, freeLimit);

  const scenarioMutation = useMutation({
    mutationFn: async () => {
      const relatedSymbols = [...new Set(conflict.sectors.flatMap((s) => s.relatedSymbols))];
      const relatedMarket = (marketQuotes ?? []).filter((q: any) => relatedSymbols.includes(q.symbol));

      const { data, error } = await supabase.functions.invoke("economic-impact-ai", {
        body: {
          conflict: conflict.name,
          sectors: conflict.sectors.map((s) => s.sector),
          marketData: relatedMarket,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (data) => setScenarios(data),
    onError: (e) => toast.error(e.message || "Failed to generate scenarios"),
  });

  const getMarketPrice = (symbol: string) => {
    const q = (marketQuotes ?? []).find((q: any) => q.symbol === symbol);
    if (!q?.price) return null;
    return q;
  };

  return (
    <section className="py-8 border-t border-primary/10">
      <div className="container mx-auto px-4">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-primary/10">
            <Zap className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Conflict Economic Impact</h2>
            <p className="text-xs text-muted-foreground">Sector analysis & AI scenario forecasts</p>
          </div>
        </div>

        {/* Conflict Selector */}
        <div className="flex flex-wrap gap-2 mb-6">
          {visibleConflicts.map((c) => (
            <button
              key={c.id}
              onClick={() => { setSelected(c.id); setScenarios(null); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all border ${
                selected === c.id
                  ? "bg-primary/15 text-primary border-primary/30"
                  : "bg-muted/50 text-muted-foreground border-transparent hover:bg-muted"
              }`}
            >
              {c.name}
              <span className="ml-1.5 text-[10px] opacity-60">{c.region}</span>
            </button>
          ))}
          {!isPremium && CONFLICTS.length > freeLimit && (
            <span className="flex items-center gap-1 px-3 py-1.5 text-xs text-muted-foreground/50">
              <Lock className="h-3 w-3" /> +{CONFLICTS.length - freeLimit} more (Premium)
            </span>
          )}
        </div>

        {/* Sector Impact Matrix */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-6">
          {conflict.sectors.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.sector}
                className="rounded-lg border border-primary/10 bg-card/50 p-4 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold text-sm text-foreground">{s.sector}</span>
                  </div>
                  <Badge variant="outline" className={`text-[10px] ${SEVERITY_COLORS[s.severity]}`}>
                    {SEVERITY_LABELS[s.severity]}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{s.description}</p>

                {/* Live market correlation */}
                {s.relatedSymbols.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1 border-t border-primary/5">
                    {s.relatedSymbols.map((sym) => {
                      const q = getMarketPrice(sym);
                      if (!q) return null;
                      const up = (q.changePercent ?? 0) >= 0;
                      return (
                        <span key={sym} className="flex items-center gap-1 text-[10px] font-mono">
                          <span className="text-muted-foreground">{q.name}:</span>
                          <span className={up ? "text-green-400" : "text-red-400"}>
                            {up ? <TrendingUp className="h-3 w-3 inline" /> : <TrendingDown className="h-3 w-3 inline" />}
                            {q.changePercent?.toFixed(2)}%
                          </span>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* AI Scenario Forecast */}
        <div className="rounded-lg border border-primary/10 bg-card/50 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <h3 className="font-semibold text-sm text-foreground">AI Scenario Forecast</h3>
            </div>
            {isPremium ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => scenarioMutation.mutate()}
                disabled={scenarioMutation.isPending}
                className="text-xs"
              >
                {scenarioMutation.isPending ? (
                  <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Analyzing...</>
                ) : (
                  <><Sparkles className="h-3 w-3 mr-1" /> Generate Scenarios</>
                )}
              </Button>
            ) : (
              <Badge variant="outline" className="text-xs bg-primary/5">
                <Lock className="h-3 w-3 mr-1" /> Premium
              </Badge>
            )}
          </div>

          {!isPremium && (
            <div className="relative">
              <div className="grid gap-3 sm:grid-cols-3 blur-sm select-none pointer-events-none">
                {["Best Case", "Base Case", "Worst Case"].map((t) => (
                  <div key={t} className="rounded-md border border-primary/10 bg-muted/30 p-3 space-y-2">
                    <span className="text-xs font-bold">{t}</span>
                    <div className="h-3 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                    <div className="h-3 bg-muted rounded w-5/6" />
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex items-center gap-2 bg-background/90 border border-primary/20 rounded-lg px-4 py-2 text-sm">
                  <Lock className="h-4 w-4 text-primary" />
                  <span className="text-foreground font-medium">Upgrade to unlock AI scenarios</span>
                </div>
              </div>
            </div>
          )}

          {isPremium && !scenarios && !scenarioMutation.isPending && (
            <p className="text-xs text-muted-foreground text-center py-6">
              Click "Generate Scenarios" to analyze economic impact with AI
            </p>
          )}

          {isPremium && scenarios?.scenarios && (
            <div className="grid gap-3 sm:grid-cols-3">
              {scenarios.scenarios.map((s: any) => {
                const colors: Record<string, string> = {
                  best: "border-green-500/30 bg-green-500/5",
                  base: "border-yellow-500/30 bg-yellow-500/5",
                  worst: "border-red-500/30 bg-red-500/5",
                };
                const titleColors: Record<string, string> = {
                  best: "text-green-400",
                  base: "text-yellow-400",
                  worst: "text-red-400",
                };
                return (
                  <div key={s.type} className={`rounded-md border p-3 space-y-2 ${colors[s.type] ?? ""}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-bold ${titleColors[s.type] ?? ""}`}>{s.title}</span>
                      <span className="text-[10px] text-muted-foreground">{s.probability}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{s.timeline}</p>
                    <p className="text-xs text-foreground/80 leading-relaxed">{s.description}</p>
                    {s.sectorImpacts?.map((si: any) => (
                      <div key={si.sector} className="flex items-center gap-1 text-[10px]">
                        <ChevronRight className="h-3 w-3 text-muted-foreground" />
                        <span className="font-medium">{si.sector}</span>
                        <span className={si.change?.startsWith("-") ? "text-green-400" : "text-red-400"}>
                          {si.change}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {isPremium && scenarios?.summary && (
            <p className="mt-4 text-xs text-muted-foreground border-t border-primary/10 pt-3 leading-relaxed">
              <AlertTriangle className="h-3 w-3 inline mr-1 text-yellow-400" />
              {scenarios.summary}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
